/**
 * Ark Tiles — sampled-product identity for the free sample line.
 *
 * Admin-clean shape (27 Oct 2026):
 *   Line item: only "Sample of" = tile title (plus "Tile name or SKU" on /products/sample).
 *   Order attribute: only "Samples ordered" = "Title A · Title B".
 * Do NOT write _Sampled products JSON, Sampled product*, or underscore handle/title/URL
 * props — Shopify Admin shows underscore keys (they do not hide).
 * Does not change sample price, £4.99 sample delivery, or the 5-per-order cap.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ArkSampleSource = api;
  if (typeof document !== 'undefined') api.install();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  var HANDLE = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
  var TITLES_KEY = 'Samples ordered';
  var LEGACY_ATTR_KEYS = [
    '_Sampled products',
    'Sampled products',
    'Sampled product handle',
    'Sampled product title',
    'Sampled product URL',
    '_Sampled product handle',
    '_Sampled product title',
    '_Sampled product URL',
  ];
  var LEGACY_LINE_KEYS = [
    'Sampled product handle',
    'Sampled product title',
    'Sampled product URL',
    '_Sampled product handle',
    '_Sampled product title',
    '_Sampled product URL',
  ];

  function text(value) {
    return String(value == null ? '' : value);
  }

  function safeHandle(value) {
    var handle = text(value).trim().toLowerCase();
    return HANDLE.test(handle) ? handle : '';
  }

  function safeVariantId(value) {
    var match = text(value).trim().match(/^\d+$/);
    return match ? match[0] : '';
  }

  function normalizeTitle(value) {
    return text(value).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function originOf(url) {
    var match = text(url).match(/^(https?:\/\/[^/]+)/i);
    return match ? match[1] : '';
  }

  function productUrl(origin, handle, variantId) {
    var safe = safeHandle(handle);
    var base = text(origin).replace(/\/$/, '');
    if (!safe || !/^https?:\/\//i.test(base)) return '';
    var url = base + '/products/' + safe;
    var id = safeVariantId(variantId);
    if (id) url += '?variant=' + id;
    return url;
  }

  function exactTitleMatch(products, title) {
    var want = normalizeTitle(title);
    if (!want) return null;
    var list = Array.isArray(products) ? products : [];
    for (var i = 0; i < list.length; i++) {
      if (normalizeTitle(list[i] && list[i].title) === want) return list[i];
    }
    return null;
  }

  function storefrontBlocked(product) {
    if (!product || typeof product !== 'object') return { blocked: true, reason: 'draft' };
    if (!product.published_at) return { blocked: true, reason: 'draft' };
    if (product.available === false) return { blocked: true, reason: 'out_of_stock' };
    return { blocked: false, reason: 'ok' };
  }

  function pickSimilar(products, currentHandle) {
    var skip = safeHandle(currentHandle);
    var list = Array.isArray(products) ? products : [];
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item || item.available === false) continue;
      var handle = safeHandle(item.handle);
      if (!handle || handle === 'sample' || handle === skip) continue;
      var url = text(item.url || '');
      if (!url) url = '/products/' + handle;
      return { title: text(item.title), url: url };
    }
    return null;
  }

  function selectedVariant(product, root) {
    if (!product || !root || !root.querySelector) return null;
    var input = root.querySelector('[name="id"]');
    var id = input && input.value;
    if (!id) return null;
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(id)) return variants[i];
    }
    return null;
  }

  /** No Admin clutter: PDP add merges this into line properties — return empty. */
  function trackedProperties(product, guard, root) {
    return {};
  }

  function isSampleLine(item) {
    if (!item) return false;
    return (
      item.variant_id === 58278074548569 ||
      item.product_id === 16352252035417 ||
      String(item.sku || '').toLowerCase() === 'sample' ||
      item.handle === 'sample'
    );
  }

  function titlesFromCart(cart) {
    var titles = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!isSampleLine(item)) continue;
      var props = item.properties || {};
      var title = text(props['Sample of'] || props['Tile name or SKU']).trim();
      if (!title) continue;
      if (titles.indexOf(title) === -1) titles.push(title);
    }
    return titles;
  }

  function noteAttributes(existing, entry, cart) {
    var next = {};
    var source = existing && typeof existing === 'object' ? existing : {};
    var key;
    for (key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) next[key] = source[key];
    }
    for (var li = 0; li < LEGACY_ATTR_KEYS.length; li++) {
      next[LEGACY_ATTR_KEYS[li]] = '';
    }
    var titles = titlesFromCart(cart);
    var entryTitle = text(entry && entry.title).trim();
    if (entryTitle && titles.indexOf(entryTitle) === -1) titles.push(entryTitle);
    next[TITLES_KEY] = titles.length ? titles.join(' · ') : '';
    return next;
  }

  function readGuard(scope) {
    var doc = scope && scope.querySelectorAll ? scope : typeof document !== 'undefined' ? document : null;
    if (!doc || !doc.querySelectorAll) return null;
    var nodes = doc.querySelectorAll('[data-ark-sample-source]');
    var el = null;
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].closest && nodes[i].closest('.sticky-add-to-cart')) continue;
      el = nodes[i];
      break;
    }
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '');
    } catch (error) {
      return null;
    }
  }

  function stampCart(cart, props) {
    // Always re-read /cart.js: PDP caller passes the pre-add cart snapshot, and
    // trackedProperties is intentionally empty (no Admin clutter on the line).
    if (typeof fetch !== 'function') return Promise.resolve(null);
    var title = text(props && (props['Sample of'] || props.title || props['_Sampled product title'])).trim();
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (fresh) {
        var attributes = noteAttributes(
          (fresh && fresh.attributes) || (cart && cart.attributes),
          title ? { title: title } : null,
          fresh || cart
        );
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
        });
      })
      .catch(function () {
        return null;
      });
  }

  function field(root, name) {
    return root.querySelector('[data-ark-sampled="' + name + '"]');
  }

  function setField(input, value) {
    if (!input) return;
    if (value) {
      input.disabled = false;
      input.value = value;
    } else {
      input.value = '';
      input.disabled = true;
    }
  }

  function applyTitlesAttr(root, cart, title) {
    var attributes = noteAttributes(cart && cart.attributes, title ? { title: title } : null, cart);
    setField(field(root, 'attr-titles'), attributes[TITLES_KEY] || '');
  }

  function showUnavailable(root, similar) {
    var note = root.querySelector('[data-ark-sample-unavailable]');
    if (!note) return;
    note.hidden = false;
    note.textContent = '';
    var message = document.createElement('span');
    message.textContent = similar
      ? "This tile isn't available to sample."
      : "This tile isn't available to sample right now.";
    note.appendChild(message);
    if (similar && similar.url) {
      var link = document.createElement('a');
      link.href = similar.url;
      link.textContent = similar.title ? 'View ' + similar.title : 'View a similar product in stock';
      note.appendChild(document.createTextNode(' '));
      note.appendChild(link);
    }
  }

  function installSampleForm(root) {
    if (!root || root.getAttribute('data-ark-sample-source-ready') === 'true') return;
    root.setAttribute('data-ark-sample-source-ready', 'true');
    var visible = root.querySelector('#sample-tile-name');
    var sampleOf = field(root, 'sample-of');
    var params = new URLSearchParams(window.location.search);
    var requestedHandle = safeHandle(params.get('handle'));
    var requestedVariant = safeVariantId(params.get('variant'));
    var requestedTitle = params.get('tile') || '';
    var linkedTitle = '';
    var cartSnapshot = null;

    function syncSampleOf() {
      if (!sampleOf || !visible) return;
      sampleOf.disabled = false;
      sampleOf.value = visible.value;
      applyTitlesAttr(root, cartSnapshot, visible.value);
    }

    if (visible) visible.addEventListener('input', function () {
      syncSampleOf();
      if (linkedTitle && normalizeTitle(visible.value) !== normalizeTitle(linkedTitle)) {
        linkedTitle = '';
      }
    });

    function accept(product) {
      var variantId = requestedVariant;
      if (variantId) {
        var variants = product.variants || [];
        var found = null;
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === variantId) found = variants[i];
        }
        if (!found || found.available === false) variantId = '';
      }
      linkedTitle = product.title;
      if (visible && (!visible.value || normalizeTitle(visible.value) === normalizeTitle(requestedTitle) || normalizeTitle(visible.value) === normalizeTitle(product.title))) {
        visible.value = product.title;
      }
      syncSampleOf();
    }

    function reject(product) {
      if (visible && requestedTitle && normalizeTitle(visible.value) === normalizeTitle(requestedTitle)) visible.value = '';
      if (visible && product && normalizeTitle(visible.value) === normalizeTitle(product.title)) visible.value = '';
      syncSampleOf();
      if (!product || !product.id) {
        showUnavailable(root, null);
        return;
      }
      fetch('/recommendations/products.json?product_id=' + encodeURIComponent(product.id) + '&limit=8&intent=related', {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (data) {
          showUnavailable(root, pickSimilar(data && data.products, product.handle));
        })
        .catch(function () { showUnavailable(root, null); });
    }

    function consider(product, missing) {
      if (missing) {
        reject(null);
        return;
      }
      if (!product) return;
      var state = storefrontBlocked(product);
      if (state.blocked) reject(product);
      else accept(product);
    }

    var cartReady = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) { cartSnapshot = cart; applyTitlesAttr(root, cart, visible && visible.value); })
      .catch(function () { cartSnapshot = null; });

    cartReady.then(function () {
      if (requestedHandle) {
        return fetch('/products/' + requestedHandle + '.js', { headers: { Accept: 'application/json' } })
          .then(function (response) {
            if (response.status === 404) {
              consider(null, true);
              return null;
            }
            return response.ok ? response.json() : null;
          })
          .then(function (product) {
            if (product) consider(product, false);
          })
          .catch(function () { consider(null, true); });
      }
      if (!requestedTitle) return null;
      return fetch('/search/suggest.json?q=' + encodeURIComponent(requestedTitle) + '&resources[type]=product&resources[limit]=8', {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (data) {
          var products = data && data.resources && data.resources.results && data.resources.results.products;
          var match = exactTitleMatch(products, requestedTitle);
          if (!match || !safeHandle(match.handle)) return null;
          return fetch('/products/' + safeHandle(match.handle) + '.js', { headers: { Accept: 'application/json' } });
        })
        .then(function (response) {
          if (!response) return null;
          if (response.status === 404) {
            consider(null, true);
            return null;
          }
          return response.ok ? response.json() : null;
        })
        .then(function (product) {
          if (product) consider(product, false);
        })
        .catch(function () {});
    });

    document.addEventListener('cart:update', function () {
      var title = (sampleOf && sampleOf.value) || (visible && visible.value) || '';
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          cartSnapshot = cart;
          return stampCart(cart, { 'Sample of': title });
        })
        .catch(function () {});
    });
  }

  function install() {
    if (typeof document === 'undefined') return;
    var run = function () {
      var forms = document.querySelectorAll('[data-ark-sample-tile-property]');
      for (var i = 0; i < forms.length; i++) installSampleForm(forms[i]);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
  }

  return {
    safeHandle: safeHandle,
    safeVariantId: safeVariantId,
    normalizeTitle: normalizeTitle,
    productUrl: productUrl,
    exactTitleMatch: exactTitleMatch,
    storefrontBlocked: storefrontBlocked,
    pickSimilar: pickSimilar,
    trackedProperties: trackedProperties,
    noteAttributes: noteAttributes,
    titlesFromCart: titlesFromCart,
    readGuard: readGuard,
    stampCart: stampCart,
    install: install,
    LEGACY_LINE_KEYS: LEGACY_LINE_KEYS,
  };
});
